import { AiOutlineLoading } from "react-icons/ai";
import {
  Accordion,
  Alert,
  Button,
  TextInput,
  Spinner,
  Badge,
  Tooltip,
} from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { HiInformationCircle } from "react-icons/hi";
import { useQuery, useQueryClient } from "react-query";
import { FiSearch, FiCheckCircle, FiXCircle } from "react-icons/fi";

const UserQueriesForm = ({ categories, user, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [search, setSearch] = useState("");

  const queryClient = useQueryClient();
  const { isLoading: isLoadingPerms, data: userQueriesPermissions } = useQuery(
    ["userQueriesPermissions", user?.id],
    async () => {
      const { data: items } = await axios.get(
        `${USERS.GET_USER_QUERY_PERMISSIONS}/${user.id}`
      );
      return items.data; // [{ QueryId }]
    },
    { enabled: isOpen && !!user?.id }
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm();
  const watchedCategories = watch("categories");

  // índice rápido dos QueryIds que o usuário já tem
  const userPermsMap = useMemo(() => {
    const map = {};
    (userQueriesPermissions || []).forEach((p) => {
      map[p.QueryId] = true;
    });
    return map;
  }, [userQueriesPermissions]);

  // Inicializa campos quando abrir / mudar permissões
  useEffect(() => {
    if (!isOpen) return;
    reset();
    (categories || []).forEach((category, catIdx) => {
      (category?.Queries || []).forEach((query, qIdx) => {
        setValue(`categories[${catIdx}].queries[${qIdx}].QueryId`, query.id);
        if (userPermsMap[query.id]) {
          setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, true);
        }
      });
    });
    setSubmitError(null);
  }, [isOpen, categories, userPermsMap, reset, setValue]);

  // Busca: normaliza pra case-insensitive
  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const searchTerm = norm(search);

  // Helpers para contagem por categoria
  const getCategoryCounts = (category, catIdx) => {
    const queries = category?.Queries || [];
    const filtered = queries.filter((q) =>
      searchTerm ? norm(q.name).includes(searchTerm) : true
    );
    const selected = filtered.filter((q, qIdx) =>
      watch(`categories[${catIdx}].queries[${qIdx}].isActive`)
    );
    return { total: filtered.length, selected: selected.length };
  };

  const totalQueries = useMemo(
    () =>
      (categories || []).reduce((acc, c) => acc + (c?.Queries?.length || 0), 0),
    [categories]
  );

  const totalSelected = useMemo(() => {
    const cats = watchedCategories || [];
    return cats.reduce(
      (acc, c) => acc + (c?.queries || []).filter((q) => q?.isActive).length,
      0
    );
  }, [watchedCategories]);

  const selectAllFiltered = () => {
    (categories || []).forEach((category, catIdx) => {
      (category?.Queries || []).forEach((q, qIdx) => {
        if (!searchTerm || norm(q.name).includes(searchTerm)) {
          setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, true);
        }
      });
    });
  };

  const clearAllFiltered = () => {
    (categories || []).forEach((category, catIdx) => {
      (category?.Queries || []).forEach((q, qIdx) => {
        if (!searchTerm || norm(q.name).includes(searchTerm)) {
          setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, false);
        }
      });
    });
  };

  const selectAll = (category, catIdx) => {
    (category?.Queries || []).forEach((q, qIdx) => {
      if (!searchTerm || norm(q.name).includes(searchTerm)) {
        setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, true);
      }
    });
  };

  const clearAll = (category, catIdx) => {
    (category?.Queries || []).forEach((q, qIdx) => {
      if (!searchTerm || norm(q.name).includes(searchTerm)) {
        setValue(`categories[${catIdx}].queries[${qIdx}].isActive`, false);
      }
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // achata categorias -> queries
      const queries = (data?.categories || [])
        .flatMap((c) => c?.queries || [])
        .filter((q) => q?.isActive)
        .map((q) => ({ QueryId: q.QueryId }));

      await axios.put(
        `${USERS.GET_USER_QUERY_PERMISSIONS}/${user.id}`,
        queries
      );

      if (user?.id) {
        queryClient.invalidateQueries(["userQueriesPermissions", user.id]);
      }

      onClose(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao atualizar permissões.";
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // UI de switch bonito usando checkbox + peer (sem state extra)
  const Switch = ({ name, label, defaultChecked }) => (
    <Controller
      control={control}
      name={name}
      defaultValue={!!defaultChecked}
      render={({ field }) => (
        <label className="inline-flex items-center cursor-pointer gap-3 relative">
          <input
            type="checkbox"
            className="sr-only peer"
            ref={field.ref}
            checked={!!field.value}
            onChange={(event) => field.onChange(event.target.checked)}
          />
          <span className="h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 peer-checked:bg-emerald-600" />
          <span className="absolute left-[2px] top-[2px] h-5 w-5 rounded-full bg-white shadow transition duration-200 peer-checked:translate-x-5" />
          {label ? (
            <span className="text-sm font-medium text-slate-700">{label}</span>
          ) : null}
        </label>
      )}
    />
  );

  // Loading
  if (isLoadingPerms) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner aria-label="Carregando permissões..." />
      </div>
    );
  }

  // Empty categories
  if (!categories || categories.length === 0) {
    return (
      <div className="text-center text-sm md:text-base">
        Não há nenhuma categoria cadastrada.
        <br />
        <span className="text-slate-500">
          Comece criando categorias e consultas.
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      {/* Header */}
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Permissões de consulta
            </div>
            <div className="text-base font-semibold text-slate-800">
              {user?.name || "Usuário"}
            </div>
            <div className="text-xs text-slate-500">{user?.email || ""}</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Badge color="gray">Selecionadas: {totalSelected}</Badge>
            <Badge color="gray">Total: {totalQueries}</Badge>
          </div>
        </div>
      </div>

      {/* Barra de busca */}
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <TextInput
              icon={FiSearch}
              type="text"
              placeholder="Buscar consulta por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="success"
              size="sm"
              type="button"
              onClick={selectAllFiltered}
            >
              <div className="flex items-center">
                <FiCheckCircle className="mr-1" />
                <span>Selecionar todas</span>
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Lista por categoria */}
      <Accordion
        collapseAll
        className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-emerald-50/40 divide-y divide-slate-200"
      >
        {categories.map((category, idxCat) => {
          const queries = category?.Queries || [];
          const filtered = queries.filter((q) =>
            searchTerm ? norm(q.name).includes(searchTerm) : true
          );
          const { total, selected } = getCategoryCounts(category, idxCat);

          return (
            <Accordion.Panel key={idxCat}>
              <Accordion.Title className="hover:bg-gray-50">
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 truncate">
                      {category.name}
                    </span>
                    <Badge color="gray" size="xs">
                      {selected}/{total}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Selecionar todas desta categoria (filtradas)">
                      <Button
                        size="xs"
                        color="success"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAll(category, idxCat);
                        }}
                        className="hidden sm:inline-flex"
                      >
                        <div className="flex items-center">
                          <FiCheckCircle className="mr-1" />
                          <span>Selecionar todas</span>
                        </div>
                      </Button>
                    </Tooltip>
                    <Tooltip content="Limpar seleção desta categoria (filtradas)">
                      <Button
                        size="xs"
                        color="light"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAll(category, idxCat);
                        }}
                        className="hidden sm:inline-flex"
                      >
                        <div className="flex items-center">
                          <FiXCircle className="mr-1" />
                          <span>Desmarcar todas</span>
                        </div>
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </Accordion.Title>

              <Accordion.Content>
                {filtered.length > 0 ? (
                  <div className="space-y-2">
                    {filtered.map((query, qIdxVisible) => {
                      // precisamos do índice real para o register:
                      const realIndex = queries.findIndex(
                        (q) => q.id === query.id
                      );
                      const nameBase = `categories[${idxCat}].queries[${realIndex}]`;
                      const isActive = !!watch(`${nameBase}.isActive`);

                      return (
                        <div
                          key={query.id}
                          className={[
                            "relative rounded-xl border bg-white p-3 shadow-sm transition overflow-hidden",
                            isActive
                              ? "border-emerald-200 ring-1 ring-emerald-200/60"
                              : "border-slate-200",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "absolute left-0 top-0 h-full w-[3px] rounded-l-xl",
                              isActive
                                ? "bg-gradient-to-b from-emerald-400 to-emerald-600"
                                : "bg-slate-200",
                            ].join(" ")}
                          />
                          {/* hidden campo QueryId */}
                          <input
                            type="hidden"
                            {...register(`${nameBase}.QueryId`)}
                            defaultValue={query.id}
                          />
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">
                                {query.name}
                              </div>
                              {query?.description ? (
                                <div className="text-xs text-slate-500 truncate">
                                  {query.description}
                                </div>
                              ) : null}
                              <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
                                <span className="uppercase tracking-wide">
                                  ID
                                </span>
                                <span className="font-semibold text-slate-600">
                                  {query.id}
                                </span>
                                <span className="text-slate-300">•</span>
                                <span
                                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isActive
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                      : "bg-slate-100 text-slate-600 border border-slate-200"
                                  }`}
                                >
                                  {isActive ? "Ativa" : "Inativa"}
                                </span>
                              </div>
                            </div>
                            <div className="shrink-0">
                              <Switch
                                name={`${nameBase}.isActive`}
                                label=""
                                defaultChecked={!!userPermsMap[query.id]}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-slate-500">
                    Nenhuma consulta encontrada para este filtro.
                  </div>
                )}
              </Accordion.Content>
            </Accordion.Panel>
          );
        })}
      </Accordion>

      {/* Footer fixo */}
      <div className="sticky bottom-0 mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white/80 p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        {submitError && (
          <Alert color="failure" icon={HiInformationCircle}>
            {submitError}
          </Alert>
        )}
        <div className="flex items-center justify-end gap-3">
          <Button
            color="light"
            type="button"
            onClick={() => onClose(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="primary"
            isProcessing={isSubmitting}
            processingSpinner={
              <AiOutlineLoading className="h-6 w-6 animate-spin" />
            }
            disabled={isSubmitting}
          >
            Atualizar permissões
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserQueriesForm;
